// src/FaqPage.js
import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';

const faqData = [
    {
        question: 'How do I earn spins?',
        answer: (
            <>
                You can earn spins by completing tasks and offers from our trusted partners.
                Navigate to the <Link component={RouterLink} to="/offers" underline="hover">Earn Spins</Link> page
                to see available offers. Make sure to follow the instructions for each offer carefully.
                Spins are usually credited shortly after the task provider confirms completion. <br /><br />
                While 99% of offers are completely free, some may require a small purchase to complete.
                These premium offers typically reward significantly more, often providing 20-30 spins or high-value items.
            </>
        ),
    },
    {
        question: 'What kind of items can I win?',
        answer: 'You can win a variety of Counter-Strike items, including weapon skins, agents, cases, knives, or gloves. Every spin guarantees a win, and the item is added directly to your on-site inventory.',
    },
    {
        question: 'How do I withdraw my winnings?',
        answer: (
            <>
                Once you have items in your <Link component={RouterLink} to="/inventory" underline="hover">Inventory</Link>,
                you can withdraw them to your Steam account. Ensure your Steam Trade URL is correctly set up in your
                {' '}<Link component={RouterLink} to="/profile" underline="hover">Profile</Link>.
                Select the items you wish to withdraw and follow the prompts. Withdrawals are processed via Steam Trade Offers.
            </>
        ),
    },
    {
        question: 'Is SpinnerCS free to play?',
        answer: 'Yes, SpinnerCS is 100% free to play. You earn spins by completing offers, not by depositing money.',
    },
    {
        question: 'Why haven\'t I received spins for an offer I completed?',
        answer: (
            <>
                Spin crediting depends on confirmation from the third-party offer provider. This can sometimes take a while (from minutes to several hours, or even longer in rare cases).
                Ensure you met all offer requirements, such as being a new user for an app or reaching a specific level in a game.
                If it has been an unusually long time, you may need to contact the support for the specific offer wall you used.
                You can find more details on the <Link component={RouterLink} to="/offers" underline="hover">Earn Spins</Link> page.
            </>
        ),
    },
    {
        question: 'Is my Steam account safe?',
        answer: 'We use Steam\'s official OpenID system for login, which means we never receive or store your Steam password. Your account security is a top priority. Ensure your Steam Trade URL is correct and your Steam inventory is set to public for withdrawals.',
    },
    {
        question: 'What is a Steam Trade URL and where do I find it?',
        answer: (
            <>
                Your Steam Trade URL allows other users (and services like ours) to send you trade offers without needing to be friends on Steam.
                You can find or create your Trade URL on the Steam website.
                Go to your Steam Inventory &gt; Trade Offers &gt; Who can send me Trade Offers? &gt; Scroll down to "Third-Party Sites".
                You can set this URL in your <Link component={RouterLink} to="/profile" underline="hover">Profile</Link> on SpinnerCS.
            </>
        ),
    },
    {
        question: 'How does the referral system work?',
        answer: (
            <>
                You can invite friends using your unique referral code or link found on the
                {' '}<Link component={RouterLink} to="/offers" underline="hover">Earn Spins</Link> page (Referral section).
                You receive bonus spins when your referred friend signs up and completes their first task. Your friend also gets a bonus spin for signing up with your referral.
            </>
        ),
    },
    // Add more FAQs as needed
];

function FaqPage() {
    // Set Document Title
    useEffect(() => {
        document.title = 'SpinnerCS - FAQ';
    }, []);

    const [expanded, setExpanded] = React.useState(false);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ bgcolor: 'background.paper', p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2 }}>
                <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 4 }}>
                    Frequently Asked Questions
                </Typography>

                {faqData.map((faq, index) => (
                    <Accordion
                        key={index}
                        expanded={expanded === `panel${index}`}
                        onChange={handleChange(`panel${index}`)}
                        sx={{
                            mb: index === faqData.length - 1 ? 0 : 1.5, // Add margin bottom to all but last
                            '&:before': {
                                display: 'none', // Remove default top border
                            },
                            boxShadow: 3,
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel${index}bh-content`}
                            id={`panel${index}bh-header`}
                            sx={{
                                '& .MuiAccordionSummary-content': {
                                    // Styles for the content within the summary
                                },
                            }}
                        >
                            <Typography variant="h6" component="h3" sx={{ fontWeight: 'medium' }}>
                                {faq.question}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ bgcolor: 'action.hover' }}>
                            <Typography variant="body1" color="text.secondary">
                                {faq.answer}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                ))}

                <Typography variant="body1" sx={{ mt: 4, textAlign: 'center' }}>
                    Can't find the answer you're looking for?
                    Please refer to our{' '}
                    <Link component={RouterLink} to="/terms" underline="hover">
                        Terms of Service
                    </Link>
                    {' '}or{' '}
                    <Link component={RouterLink} to="/privacy" underline="hover">
                        Privacy Policy
                    </Link>
                    .
                    {/* TODO: Add a contact method if available, e.g., "or contact our support team." */}
                </Typography>
            </Box>
        </Container>
    );
}

export default FaqPage;